import { useCallback, useEffect, useMemo } from 'react';

import { ServiceAttendanceObject, ServiceBillingInfo } from '../../../@types/service.type';
import { Folders } from '../../../@types/uploaded-files.type';
import { NodeAttributeType } from '../../../configs/enums';
import { useGetServiceLogsByServiceId, useAddServiceLogsForService } from '../../../queries/service-log-query';
import {
  useGetServiceById, useGetAllServiceAttendance,
  useGetOtherServicesUnderMainLocation, useGetLinkedServices,
  useCreateOrUpdateServiceAttributes, useUpdateService,
  useGetBillingInfo, useUpdateBillingInfo,
} from '../../../queries/service-query';
import { useGetNodeAttributesByCustomRange, useGetParentAttributesByPropertyType } from '../../../queries/structure-query';
import { useGetUploadedImages } from '../../../queries/uploadedfiles-query';
import { trackJobAccess } from '../../../utils/recent-jobs';

const buildAttendanceMetrics = (attendance: ServiceAttendanceObject[]) => {
  const totalVisits = attendance.length;
  const uniqueTechs = new Set(attendance.map((a) => a.siblingNodeName)).size;
  const durations = attendance.map((a) => {
    const checkin = new Date(a.checkInDateTime).getTime();
    const checkout = new Date(a.checkOutDateTime).getTime();
    return checkout - checkin;
  }).filter((d) => d > 0);
  const totalMs = durations.reduce((sum, d) => sum + d, 0);
  const avgMs = totalVisits > 0 ? totalMs / totalVisits : 0;
  const formatDuration = (ms: number) => {
    const hours = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };
  const sorted = [...attendance].sort(
    (a, b) => new Date(a.checkInDateTime).getTime() - new Date(b.checkInDateTime).getTime(),
  );
  return {
    totalVisits,
    onSiteTime: formatDuration(totalMs),
    avgDuration: formatDuration(avgMs),
    uniqueTechs,
    firstVisit: sorted[0]?.checkInDateTime ? new Date(sorted[0].checkInDateTime).toLocaleDateString() : undefined,
    lastVisit: sorted.at(-1)?.checkInDateTime ? new Date(sorted.at(-1)!.checkInDateTime).toLocaleDateString() : undefined,
  };
};

export const useServiceDetailData = (serviceId: number) => {
  const serviceQuery = useGetServiceById(serviceId);
  const logsQuery = useGetServiceLogsByServiceId(serviceId);
  const attendanceQuery = useGetAllServiceAttendance(serviceId);
  const addLogMutation = useAddServiceLogsForService(serviceId);

  const service = serviceQuery.data ?? null;
  const contractId = service?.contractId ?? -1;
  const clientLogoQuery = useGetUploadedImages(contractId, Folders.ClientContractLogos);
  const clientLogoPic = clientLogoQuery.data?.[0]?.url ?? undefined;
  const nodeId = service?.mainLocationNodeId ?? 0;
  const relatedJobsQuery = useGetOtherServicesUnderMainLocation(nodeId, { page: 0, pageSize: 10 });
  const linkedServicesQuery = useGetLinkedServices(serviceId);
  const nodeAttributeTypes = useMemo(
    () => [NodeAttributeType.Address, NodeAttributeType.Contact, NodeAttributeType.TradingHours, NodeAttributeType.ReferenceNumbers],
    [],
  );
  const nodeAttrsQuery = useGetNodeAttributesByCustomRange(
    String(nodeId), nodeAttributeTypes,
    service?.isSiblingInServiceClientHierarchy ? undefined : serviceId,
  );
  const tradingHoursQuery = useGetParentAttributesByPropertyType(
    String(nodeId), NodeAttributeType.TradingHours,
    service?.isSiblingInServiceClientHierarchy ? undefined : serviceId,
  );
  const coverageHoursQuery = useGetParentAttributesByPropertyType(
    String(nodeId), NodeAttributeType.Coverages,
    service?.isSiblingInServiceClientHierarchy ? undefined : serviceId,
  );

  useEffect(() => {
    if (nodeId) {
      tradingHoursQuery.refetch();
      coverageHoursQuery.refetch();
    }
  }, [nodeId]);

  // Track this job as recently accessed
  useEffect(() => {
    if (service) {
      trackJobAccess({
        id: service.id,
        jobId: `JOB-${service.id}`,
        client: typeof service.client === 'string' ? service.client : '',
        location: typeof service.location === 'string' ? service.location : '',
      });
    }
  }, [service?.id]);

  const logs = useMemo(
    () => (logsQuery.data as unknown as Array<Record<string, unknown>>) ?? [],
    [logsQuery.data],
  );

  const attendanceData = useMemo(() => attendanceQuery.data ?? [], [attendanceQuery.data]);
  const clientContractIds = useMemo(() => {
    try { return JSON.parse(service?.clientHierarchy ?? '[]') as number[]; }
    catch { return [] as number[]; }
  }, [service?.clientHierarchy]);
  const attendanceMetrics = useMemo(() => buildAttendanceMetrics(attendanceData), [attendanceData]);

  const billingInfoQuery = useGetBillingInfo(serviceId);
  const billingInfoMutation = useUpdateBillingInfo(serviceId);

  useEffect(() => {
    if (serviceId > 0) billingInfoQuery.refetch();
  }, [serviceId]);

  const billingInfoData = billingInfoQuery.data ?? null;
  const billingInfo = service?.adminDetails?.billingInfo;
  const billing = useMemo(() => billingInfo ? {
    labourCost: 0, partsCost: 0, calloutFee: 0, total: 0,
  } : undefined, [billingInfo]);
  const invoices = billingInfoData?.invoices ?? billingInfo?.invoices ?? [];
  const internalInvoices = useMemo(
    () => invoices.filter((inv) => inv.invoiceType === 'internal') as unknown as Array<Record<string, unknown>>,
    [invoices],
  );
  const externalInvoices = useMemo(
    () => invoices.filter((inv) => inv.invoiceType !== 'internal') as unknown as Array<Record<string, unknown>>,
    [invoices],
  );

  const otherJobsAtLocation = useMemo(
    () => (relatedJobsQuery.data?.data as unknown as Array<Record<string, unknown>>) ?? [],
    [relatedJobsQuery.data],
  );

  const closedJobsAtLocation = useMemo(() => {
    const closedStatuses = ['complete', 'closed', 'cancelled'];
    return otherJobsAtLocation.filter((job) => {
      const status = String(job.status ?? job.serviceStatusName ?? '').toLowerCase();
      return closedStatuses.includes(status);
    });
  }, [otherJobsAtLocation]);

  const linkedJobs = useMemo(() => {
    const data = linkedServicesQuery.data;
    if (!data) return [];
    const children = (data as unknown as Record<string, unknown>).childServices as Array<Record<string, unknown>> | undefined;
    const parent = (data as unknown as Record<string, unknown>).parentService as Record<string, unknown> | undefined;
    const jobs: Array<Record<string, unknown>> = [];
    if (parent?.id) jobs.push(parent);
    if (children?.length) jobs.push(...children);
    return jobs;
  }, [linkedServicesQuery.data]);

  const saveAttributeMutation = useCreateOrUpdateServiceAttributes();
  const updateServiceMutation = useUpdateService(serviceId);

  const handleFieldUpdate = useCallback((field: string, value: string) => {
    if (!service) return;
    const baseData = {
      id: serviceId,
      serviceTypeCode: service.serviceTypeCode ?? '',
      serviceStatusCode: service.serviceStatusCode ?? service.serviceStatus?.code ?? '',
      actionTypeCode: service.actionTypeCode ?? '',
      processTypeCode: service.processTypeCode ?? '',
      nodeId: service.mainLocationNodeId ?? 0,
      priorityCode: String(service.priorityCode ?? ''),
      subLocationNodeId: service.subLocationNodeId ?? undefined,
    };
    if (field === 'scheduledAt') updateServiceMutation.mutate({ ...baseData, scheduledAt: value });
    else if (field === 'followUpAt') updateServiceMutation.mutate({ ...baseData, followUpAt: value });
  }, [service, serviceId, updateServiceMutation]);

  const handleSaveAddress = useCallback((value: Record<string, unknown>) => {
    saveAttributeMutation.mutate({ serviceId, attributeTypeCode: 'address', name: 'Address', value });
  }, [serviceId, saveAttributeMutation]);

  const handleAddNote = useCallback((note: string, isInternal: boolean) => {
    addLogMutation.mutate({ notes: note, isInternal });
  }, [addLogMutation]);

  const handleSaveBillingInfo = useCallback((data: ServiceBillingInfo) => {
    billingInfoMutation.mutate(data, {
      onSuccess: () => {
        billingInfoQuery.refetch();
        serviceQuery.refetch();
      },
    });
  }, [billingInfoMutation, billingInfoQuery, serviceQuery]);

  const handleRefresh = useCallback(() => {
    serviceQuery.refetch();
    logsQuery.refetch();
  }, [serviceQuery, logsQuery]);

  return {
    service, serviceQuery, logs, clientLogoPic, nodeId,
    nodeAttrsQuery, tradingHoursQuery, coverageHoursQuery,
    attendanceData, attendanceMetrics, clientContractIds,
    billing, billingInfoData, internalInvoices, externalInvoices,
    otherJobsAtLocation, closedJobsAtLocation, linkedJobs,
    handleFieldUpdate, handleSaveAddress, handleAddNote, handleSaveBillingInfo, handleRefresh,
  };
};
